import * as React from "react";
import {
  PresentationView,
  PropertyPhoto,
  SectionView,
} from "@cma/presentation-generation-logic/models";
import { HeroHeaderView } from "@cma/chat-widgets/cma/components/presentation-sections/HeroHeader/HeroHeader.view";
import { KeyFeaturesView } from "@cma/chat-widgets/cma/components/presentation-sections/KeyFeatures/KeyFeatures.view";
import { KeyFeaturesPrint } from "@cma/chat-widgets/cma/components/presentation-sections/KeyFeatures/KeyFeatures.print";
import { SideBySideComparisonView } from "@cma/chat-widgets/cma/components/presentation-sections/SideBySideComparison/SideBySideComparison.view";
import { SideBySideComparisonPrint } from "@cma/chat-widgets/cma/components/presentation-sections/SideBySideComparison/SideBySideComparison.print";
import { InsightsView } from "@cma/chat-widgets/cma/components/presentation-sections/Insights/Insights.view";
import { InsightsPrint } from "@cma/chat-widgets/cma/components/presentation-sections/Insights/Insights.print";
import { FreeHtmlTextView } from "@cma/chat-widgets/cma/components/presentation-sections/FreeHtmlText/FreeHtmlText.view";
import { FreeHtmlTextPrint } from "@cma/chat-widgets/cma/components/presentation-sections/FreeHtmlText/FreeHtmlText.print";
import { NeighborhoodMap } from "@cma/chat-widgets/cma/components/presentation-sections/NeighborhoodMap";
import { NeighborhoodMapPrint } from "@cma/chat-widgets/cma/components/presentation-sections/NeighborhoodMap/NeighborhoodMap.print";
import { BrandingView } from "@cma/chat-widgets/cma/components/presentation-sections/Branding/Branding.view";
import { BrandingPrint } from "@cma/chat-widgets/cma/components/presentation-sections/Branding/Branding.print";
import { TeamBrandingView } from "@cma/chat-widgets/cma/components/presentation-sections/TeamBranding/TeamBranding.view";
import { TeamBrandingPrint } from "@cma/chat-widgets/cma/components/presentation-sections/TeamBranding/TeamBranding.print";
import { HeroHeaderPrint } from "@cma/chat-widgets/cma/components/presentation-sections/HeroHeader/HeroHeader.print";

interface Props {
  sectionView: SectionView;
  agent: PresentationView["agent"];
  galleries: { [key: string]: PropertyPhoto[] };
  displayMode: "mobile" | "desktop" | "print" | "tablet";
}

export const PresentationViewSectionRenderer = React.memo((props: Props) => {
  const { sectionView, galleries, agent, displayMode } = props;

  if (sectionView.type === "header") {
    return displayMode === "print" ? (
      <HeroHeaderView sectionView={sectionView} agent={agent} />
    ) : (
      <HeroHeaderView sectionView={sectionView} agent={agent} />
    );
  }

  if (sectionView.type === "header-branding") {
    return displayMode === "print" ? (
      <HeroHeaderPrint sectionView={sectionView} agent={agent} />
    ) : (
      <HeroHeaderView sectionView={sectionView} agent={agent} />
    );
  }

  if (sectionView.type === "key-features") {
    return displayMode === "print" ? (
      <KeyFeaturesPrint sectionView={sectionView} />
    ) : (
      <KeyFeaturesView sectionView={sectionView} />
    );
  }

  if (sectionView.type === "side-by-side-comparision") {
    return displayMode === "print" ? (
      <SideBySideComparisonPrint
        sectionView={sectionView}
        galleries={galleries}
      />
    ) : (
      <SideBySideComparisonView
        sectionView={sectionView}
        galleries={galleries}
      />
    );
  }

  if (sectionView.type === "insights") {
    return displayMode === "print" ? (
      <InsightsPrint sectionView={sectionView} />
    ) : (
      <InsightsView sectionView={sectionView} />
    );
  }

  if (sectionView.type === "free-html-text") {
    return displayMode === "print" ? (
      <FreeHtmlTextPrint sectionView={sectionView} />
    ) : (
      <FreeHtmlTextView sectionView={sectionView} />
    );
  }

  if (sectionView.type === "ai-assisted-text") {
    return displayMode === "print" ? (
      <FreeHtmlTextPrint sectionView={sectionView} />
    ) : (
      <FreeHtmlTextView sectionView={sectionView} />
    );
  }

  if (sectionView.type === "neighborhood-map") {
    return displayMode === "print" ? (
      <NeighborhoodMapPrint sectionView={sectionView} galleries={galleries} />
    ) : (
      <NeighborhoodMap sectionView={sectionView} galleries={galleries} />
    );
  }

  if (sectionView.type === "branding") {
    return displayMode === "print" ? (
      <BrandingPrint sectionView={sectionView} />
    ) : (
      <BrandingView sectionView={sectionView} displayMode={displayMode} />
    );
  }

  if (sectionView.type === "team-branding") {
    return displayMode === "print" ? (
      <TeamBrandingPrint sectionView={sectionView} />
    ) : (
      <TeamBrandingView sectionView={sectionView} displayMode={displayMode} />
    );
  }

  console.warn(
    `PresentationViewSectionRenderer: Section type "${sectionView.type}" not implemented.`,
  );
  return null;
});

PresentationViewSectionRenderer.displayName = "PresentationViewSectionRenderer";
